import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import { useNotification } from "../../../context";
import {
  isOcConflict,
  isOcForbidden,
  useRejectOc,
} from "../../../hooks/useOcActions";
import "./editOc.scss";

interface RejectOcFormFields {
  reason: string;
  detail: string;
}

const EMPTY: RejectOcFormFields = { reason: "", detail: "" };

export interface RejectOcModalProps {
  open: boolean;
  onClose: () => void;
  ocId: string;
}

/**
 * "Rechazar": the approver's reject dialog. The backend requires a non-blank reason, so the submit
 * stays disabled until one is typed; the optional detail is sent only when filled. A 403 (not the
 * designated approver) or 409 (no longer pending) is an error toast, never a crash.
 */
const RejectOcModal = ({ open, onClose, ocId }: RejectOcModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const reject = useRejectOc();
  const { control, handleSubmit, reset, watch } = useForm<RejectOcFormFields>({
    defaultValues: EMPTY,
  });

  // Every open starts from an empty form.
  useEffect(() => {
    if (open) reset(EMPTY);
  }, [open, reset]);

  const reasonBlank = watch("reason").trim() === "";

  const onSubmit = (values: RejectOcFormFields) => {
    if (values.reason.trim() === "") return;
    reject.mutate(
      {
        id: ocId,
        reason: values.reason.trim(),
        detail: values.detail.trim() || undefined,
      },
      {
        onSuccess: () => {
          openNotification(t("purchaseOrders.reject.success"));
          onClose();
        },
        onError: (error) => {
          openNotification(
            t(
              isOcForbidden(error)
                ? "purchaseOrders.reject.notApprover"
                : isOcConflict(error)
                  ? "purchaseOrders.reject.notPending"
                  : "purchaseOrders.reject.error"
            )
          );
          // The OC is refetched on settle; a 403/409 means the dialog is moot.
          if (isOcForbidden(error) || isOcConflict(error)) onClose();
        },
      }
    );
  };

  const title = t("purchaseOrders.reject.title");

  return (
    <ComposedModal open={open} size="md" aria-label={title} onClose={onClose}>
      <ModalHeader title={title} />
      <ModalBody>
        <form
          id="reject-oc-form"
          className="edit-oc__form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <TextareaField
            name="reason"
            label={t("purchaseOrders.reject.reason")}
            control={control}
            rows={3}
          />
          <TextareaField
            name="detail"
            label={t("purchaseOrders.reject.detail")}
            control={control}
            rows={3}
            optional
          />
        </form>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={t("purchaseOrders.reject.submit")}
        danger
        loadingStatus={reject.isPending ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        primaryButtonDisabled={reject.isPending || reasonBlank}
        onRequestSubmit={handleSubmit(onSubmit)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default RejectOcModal;
